"use client";

import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import type { Locale } from "@/i18n/config";
import type { Dictionary } from "@/i18n/types";
import { cn } from "@/lib/cn";
import { WaitlistForm, type FormText } from "./waitlist-form";

type Store = "android" | "ios";

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className} fill="currentColor">
      <path d="M4.5 2.6a1 1 0 0 1 1.04-.05l14.2 8.2a1.4 1.4 0 0 1 0 2.5l-14.2 8.2A1 1 0 0 1 4 20.6V3.4a1 1 0 0 1 .5-.8Z" />
    </svg>
  );
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className} fill="currentColor">
      <path d="M16.37 12.6c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.7-3.18-1.73-1.35-.14-2.64.8-3.33.8-.69 0-1.74-.78-2.86-.76-1.47.02-2.83.86-3.59 2.17-1.53 2.66-.39 6.6 1.1 8.75.73 1.05 1.6 2.24 2.73 2.2 1.1-.05 1.51-.71 2.84-.71 1.32 0 1.7.71 2.86.69 1.18-.02 1.93-1.07 2.65-2.13.84-1.22 1.18-2.4 1.2-2.46-.03-.01-2.3-.88-2.32-3.5ZM14.2 6.13c.6-.73 1.01-1.75.9-2.76-.87.04-1.92.58-2.54 1.31-.56.65-1.05 1.68-.92 2.67.97.08 1.96-.49 2.56-1.22Z" />
    </svg>
  );
}

/**
 * Botões das lojas. Com link (src/content/site.ts → storeLinks) levam direto
 * à loja; sem link, abrem um popup honesto de "Em breve" com o formulário.
 * <dialog> nativo: fecha com Esc, prende o foco e é lido por leitores de tela.
 */
export function StoreButtons({
  t,
  form,
  locale,
  privacyHref,
  links,
  tone = "dark",
  className,
}: {
  t: Dictionary["stores"];
  form: FormText;
  locale: Locale;
  privacyHref: string;
  links: { googlePlay: string | null; appStore: string | null };
  /** "dark": botões escuros em fundo claro; "light": contorno claro em fundo escuro. */
  tone?: "dark" | "light";
  className?: string;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [store, setStore] = useState<Store | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (store && !dialog.open) {
      dialog.showModal();
      document.documentElement.style.overflow = "hidden";
    }
    if (!store && dialog.open) dialog.close();
  }, [store]);

  function onClosed() {
    setStore(null);
    document.documentElement.style.overflow = "";
  }

  const buttons = [
    { store: "android" as const, name: "Google Play", href: links.googlePlay, Icon: PlayIcon },
    { store: "ios" as const, name: "App Store", href: links.appStore, Icon: AppleIcon },
  ];

  const buttonClass = cn(
    "inline-flex min-h-14 cursor-pointer items-center gap-3 rounded-2xl px-5 py-2 text-left transition-colors",
    tone === "light"
      ? "border-2 border-white/30 text-white hover:border-white hover:bg-white/5"
      : "bg-brand-950 text-white hover:bg-brand-800",
  );

  return (
    <>
      <div className={cn("flex flex-wrap gap-3", className)}>
        {buttons.map(({ store: key, name, href, Icon }) => {
          const content = (
            <>
              <Icon className="size-7 shrink-0" />
              <span className="leading-tight">
                <span className="block text-xs font-medium tracking-wide uppercase opacity-80">
                  {href ? name : t.soon}
                </span>
                <span className="block font-display text-lg font-semibold">{name}</span>
              </span>
            </>
          );
          return href ? (
            <a key={key} href={href} target="_blank" rel="noopener" className={buttonClass}>
              {content}
            </a>
          ) : (
            <button
              key={key}
              type="button"
              onClick={() => setStore(key)}
              aria-haspopup="dialog"
              className={buttonClass}
            >
              {content}
            </button>
          );
        })}
      </div>

      <dialog
        ref={dialogRef}
        onClose={onClosed}
        // Clique fora do conteúdo (no fundo escurecido) fecha o popup.
        onClick={(e) => e.target === e.currentTarget && setStore(null)}
        aria-labelledby="loja-titulo"
        className="m-auto max-h-[92dvh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-[1.75rem] bg-brand-50 p-0 text-ink shadow-lift backdrop:bg-brand-950/70 backdrop:backdrop-blur-sm"
      >
        {store && (
          <div className="p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-brand-950 text-white">
                {store === "android" ? <PlayIcon className="size-6" /> : <AppleIcon className="size-6" />}
              </span>
              <button
                type="button"
                onClick={() => setStore(null)}
                aria-label={t.close}
                className="grid size-12 cursor-pointer place-items-center rounded-full text-brand-950 hover:bg-brand-100"
              >
                <X aria-hidden className="size-6" />
              </button>
            </div>
            <h2 id="loja-titulo" className="mt-5 text-[1.7rem] font-semibold text-brand-950">
              {store === "android" ? t.androidTitle : t.iosTitle}
            </h2>
            <p className="mt-2 text-lg text-ink-soft">
              {store === "android" ? t.androidText : t.iosText}
            </p>
            <div className="mt-6">
              <WaitlistForm
                t={form}
                locale={locale}
                privacyHref={privacyHref}
                idPrefix={`loja-${store}`}
              />
            </div>
          </div>
        )}
      </dialog>
    </>
  );
}
